/**
 * Fund partners on page 2.
 *
 * SQUAD's layout document of 11.09.2026 replaces the two-column table with one block
 * per partner, built like the Kontakt block below it: role, company, address. Four
 * partners, fixed in number but not in content — the depositary differs between the
 * German and the Luxembourg funds, which is why the addresses are data and not
 * baked into the template.
 *
 * A partner is stored as one multi-line string: first line the company, the rest its
 * address. That keeps the value a plain string, so the existing editor and every
 * saved document still work, and it mirrors how Kontakt already stores its address.
 */

/** Order and role labels, from the layout document. */
export const SQUAD_FONDSPARTNER_KEYS: Array<{ key: string; label: string }> = [
  { key: 'kvg', label: 'Kapitalverwaltungsgesellschaft' },
  { key: 'verwahrstelle', label: 'Verwahrstelle' },
  { key: 'wirtschaftspruefer', label: 'Wirtschaftsprüfer' },
  { key: 'haftungsdach', label: 'Haftungsdach' },
];

export const DEFAULT_FONDSPARTNER: Record<string, string> = {
  kvg: 'Axxion S.A.\n15, rue de Flaxweiler\nL-6776 Grevenmacher',
  verwahrstelle: 'Banque de Luxembourg S.A.\n14, Boulevard Royal\nL-2449 Luxemburg',
  wirtschaftspruefer: 'PricewaterhouseCoopers\n2, rue Gerhard Mercator\nL-2182 Luxembourg',
  haftungsdach: 'PEH Wertpapier AG\nBettinastraße 57-59\nD-60325 Frankfurt am Main',
};

/**
 * What the bare company names used to be, before addresses were part of the block.
 * A saved document that still holds exactly one of these has never been edited, so
 * it gets the address as well — otherwise every existing factsheet would render a
 * partner block with a company and no address. A name someone has changed is left
 * alone, address or not.
 */
const SUPERSEDED_FONDSPARTNER: Record<string, string> = {
  kvg: 'Axxion S.A.',
  verwahrstelle: 'Banque de Luxembourg S.A.',
  haftungsdach: 'PEH Wertpapier AG',
};

export function normalizeSquadFondspartner(
  stored: Record<string, string> | undefined,
): Record<string, string> {
  const merged: Record<string, string> = { ...DEFAULT_FONDSPARTNER, ...(stored || {}) };
  Object.entries(SUPERSEDED_FONDSPARTNER).forEach(([key, supersededName]) => {
    // Trimmed: a stray trailing space or newline is still an untouched value
    if (String(merged[key] ?? '').trim() === supersededName) merged[key] = DEFAULT_FONDSPARTNER[key];
  });
  return merged;
}

export interface SquadFondspartnerBlock {
  key: string;
  /** Role, rendered with a trailing colon. */
  label: string;
  /** Company first, address lines after. */
  lines: string[];
}

/** Blocks with something to show; a partner left empty drops out entirely. */
export function buildSquadFondspartnerBlocks(
  partners: Record<string, string> | undefined,
): SquadFondspartnerBlock[] {
  if (!partners) return [];
  return SQUAD_FONDSPARTNER_KEYS.map(({ key, label }) => ({
    key,
    label,
    lines: String(partners[key] ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean),
  })).filter((block) => block.lines.length > 0);
}
